import { CountdownButton } from '@/components/common/count-down-button';
import { HeadPhoneGuideModal } from '@/components/common/head-phone-guide-modal';
import { HeadPhoneStopModal } from '@/components/common/head-phone-stop-modal';
import { SpeakerCloseModal } from '@/components/common/speaker-close-modal';
import { UsedTTSModal } from '@/components/common/used-tts-modal';
import { ZoomButtons } from '@/components/common/zoom-buttons';
import { UsbMediaConnectStatus, WPFMessageType } from '@/enums';
import { useAccessibilityTTS, useFocusTrap, useInteractiveElementsWithTTS, useKioskResetService, useRegionNavigation, useTickSoundInteraction } from '@/hooks';
import useBaseSize from '@/hooks/useBaseSize';
import { useDisablePinchZoom } from '@/hooks/useDisablePinchZoom';
import { useKioskNavigation } from '@/hooks/useKioskNavigation';
import { useOrientation } from '@/hooks/useOrientation';
import { FooterLayout } from '@/layouts/footer/FooterLayout';
import { GNBLayout } from '@/layouts/gnb/GNBLayout';
import { Header } from '@/layouts/header/Header';
import { PATHS } from '@/router/paths';
import { CONFIG_CONSTANTS, useConfigStore, type SystemConfig, type languageType } from '@/stores';
import { buildMenuFromBuildingMenus } from '@/stores/configStore';
import type { GNBItemType } from '@/types';
import { postAudioDeviceChangeMessage } from '@/utils/post-message';
import { getHSize, getSize } from '@/utils/size';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './MainLayout.module.scss';
import { useCountdown } from '@/hooks/useCountDown';
import { ZOOM_LEVELS } from '@/constants/zoom';
import { resetService } from '@/services/resetService';
import { ChangedToHeadphoneModal } from '@/components/common/changed-to-headphone-modal';
import { DEFAULT_MENUS } from '@/constants/app';
import { useGetSvgDataQuery } from '@/services/queries/svg';

interface MainLayoutProps {
    children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const { t } = useTranslation();
    const { setMenuLayout } = useKioskNavigation();
    const { isHorizontal } = useOrientation();
    const navigate = useNavigate();
    const location = useLocation();
    const { trapFocus, releaseFocus } = useFocusTrap();
    const layoutRef = useRef<HTMLDivElement>(null);
    const ui = useConfigStore((state) => state.config?.ui);
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const zoomIn = useConfigStore((state) => state.config?.ui?.zoomIn);
    const zoomLevel = useConfigStore((state) => state.config?.ui?.zoomLevel);
    const contrast = useConfigStore((state) => state.config?.ui?.contrast);
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);
    const menu = useConfigStore((state) => state.config?.ui?.menu);
    const tts = useConfigStore((state) => state.config?.tts);
    const volume = useConfigStore((state) => state.config?.ui?.volume);
    const buildingMenus = useConfigStore((state) => state.config?.server?.buildingMenus);
    const lotteEvents = useConfigStore((state) => state.config?.server?.building?.lotteEvents);
    const barrierFree = useConfigStore((state) => state.config?.ui?.barrierFree);
    const language = useConfigStore((state) => state.config?.ui?.language);
    const storeLangs = useConfigStore((state) => state.config?.server?.store?.storeLangs);
    const hasUsedRef = useRef(false);
    const personDetectedRef = useRef(false);
    const repeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const { playTick } = useTickSoundInteraction();

    // Language check variables
    const languageCheck = {
        isEnglish: language === 'en',
        isKorean: language === 'ko',
    };

    const { speak, speakAnnouncement } = useAccessibilityTTS();
    const [focusTrapDisabled, setFocusTrapDisabled] = useState(false);
    const { baseWidth, baseHeight } = useBaseSize();
    const [isZoomButtonsOpen, setIsZoomButtonsOpen] = useState(false);
    const [isShowHeadPhoneGuide, setIsShowHeadPhoneGuide] = useState(false);
    const [isShowUsedTTSModal, setIsShowUsedTTSModal] = useState(false);
    const [isShowHeadPhoneStop, setIsShowHeadPhoneStop] = useState(false);
    const [isShowSpeakerClose, setIsShowSpeakerClose] = useState(false);
    const [isBarrierFreeUsed, setIsBarrierFreeUsed] = useState(false);
    const [isShowChangedToHeadphoneModal, setIsShowChangedToHeadphoneModal] = useState(false);
    // Function to close FooterLayout modals
    const [footerCloseModalFunction, setFooterCloseModalFunction] = useState<(() => void) | undefined>(undefined);

    // Use the combined hook for interactive elements with TTS
    useInteractiveElementsWithTTS();

    // Initialize region navigation
    useRegionNavigation({ speak });

    // Disable pinch-to-zoom gestures
    useDisablePinchZoom();

    const { startCountdown, remaining, clearCountdown } = useCountdown({
        duration: 30,
        onComplete: () => {
            setIsShowHeadPhoneStop(false);
            const currentLanguage = (storeLangs?.find((lang) => lang.mainLang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
            // Build menu using buildMenuFromBuildingMenus
            const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, currentLanguage, lotteEvents);
            updateConfig({
                ui: {
                    ...ui,
                    language: currentLanguage,
                    menu,
                    zoomIn: false,
                    zoomClosed: true,
                    contrast: false,
                    volume: 3,
                    screenMode: 'fullscreen',
                },
                tts: {
                    ...tts,
                    speaker: 'external',
                    voice: false,
                },
            });
            setIsZoomButtonsOpen(false);
            navigate(PATHS.HOME.INDEX);
        },
    });

    const isHomePage = location.pathname === PATHS.HOME.INDEX;

    // When headphone is connected, hide countdown button
    const shouldHideCountdownButton = useCallback(() => {
        return tts?.voice === true && tts?.speaker === 'headphone';
    }, [tts]);

    const handleConfigUpdate = useCallback(
        (updates: Partial<SystemConfig['ui']>) => {
            updateConfig({ ui: { ...ui, ...updates } });
        },
        [updateConfig, ui]
    );

    const handleZoomButtonsOpen = useCallback((isOpen: boolean) => {
        setIsZoomButtonsOpen(isOpen);
    }, []);

    useKioskResetService({
        isEnabled: isHomePage,
        onZoomButtonsClose: handleZoomButtonsOpen,
    });

    const handleZoomClose = useCallback(() => {
        setIsZoomButtonsOpen(false);
        handleConfigUpdate({ zoomIn: false, zoomClosed: true, zoomLevel: 1 });
        setTimeout(() => {
            document.body.style.overflow = 'hidden';
            setTimeout(() => {
                document.body.style.overflow = '';
            }, 10);
        }, 10);
    }, [handleConfigUpdate]);

    useEffect(() => {
        if (!zoomIn) {
            setIsZoomButtonsOpen(false);
        }
    }, [zoomIn]);

    const handleZoom = useCallback(() => {
        handleConfigUpdate({ zoomIn: true, zoomClosed: false, zoomLevel: zoomLevel + 1 > 4 ? 2 : zoomLevel + 1 });
    }, [handleConfigUpdate, zoomLevel]);

    const handleContrast = useCallback(() => {
        handleConfigUpdate({ contrast: !contrast });
    }, [contrast, handleConfigUpdate]);

    const gnbItems = useMemo<GNBItemType[]>(() => {
        const gnb = menu
            .filter((item) => !item?.disabled)
            .map((item) => {
                const defaultItem = DEFAULT_MENUS.find((d) => d.type === item.type);
                return {
                    ...(defaultItem ?? {}),
                    ...item,
                    icon: item.icon || defaultItem?.icon,
                };
            });

        // Create home menu item with translation
        const homeMenuItem = {
            ...DEFAULT_MENUS[0],
            label: t('common.text.home'),
            ariaLabel: t('common.text.home'),
        };

        return [homeMenuItem, ...gnb];
    }, [menu, t]);

    const { data: gnbItemsWithSvg } = useGetSvgDataQuery(gnbItems);

    useEffect(() => {
        setMenuLayout((prev) => ({ ...prev, gnb: gnbItems }));
    }, [gnbItems, setMenuLayout]);

    useEffect(() => {
        const handleDisableFocusTrap = () => setFocusTrapDisabled(true);
        const handleEnableFocusTrap = () => setFocusTrapDisabled(false);

        window.addEventListener('main-focus-trap-disable', handleDisableFocusTrap);
        window.addEventListener('main-focus-trap-enable', handleEnableFocusTrap);
        return () => {
            window.removeEventListener('main-focus-trap-disable', handleDisableFocusTrap);
            window.removeEventListener('main-focus-trap-enable', handleEnableFocusTrap);
        };
    }, []);

    useEffect(() => {
        if (layoutRef.current && !focusTrapDisabled) {
            trapFocus(layoutRef.current);
        } else if (focusTrapDisabled) {
            releaseFocus();
        }
        return () => {
            releaseFocus();
        };
    }, [trapFocus, releaseFocus, focusTrapDisabled]);

    const clearAllTimers = useCallback(() => {
        if (repeatIntervalRef.current) {
            clearInterval(repeatIntervalRef.current);
            repeatIntervalRef.current = null;
        }
    }, []);

    const startRepeatAnnouncement = useCallback(() => {
        if (repeatIntervalRef.current) {
            clearInterval(repeatIntervalRef.current);
        }

        repeatIntervalRef.current = setInterval(() => {
            if (personDetectedRef.current && !hasUsedRef.current) {
                speakAnnouncement(t('accessibility.tts.announcement_use_headphone'));
            }
        }, 60000); // 1 minute
    }, [speakAnnouncement, t]);

    const handleToggleTTS = useCallback(() => {
        if (isShowHeadPhoneGuide || isShowUsedTTSModal || isShowHeadPhoneStop || isShowSpeakerClose) {
            return;
        }

        if (tts?.voice && tts?.speaker === 'external') {
            setIsShowSpeakerClose(true);
            setIsShowHeadPhoneGuide(false);
            setIsShowUsedTTSModal(false);
            setIsShowChangedToHeadphoneModal(false);
            footerCloseModalFunction?.();
        } else if (tts?.voice) {
            setIsShowHeadPhoneGuide(true);
            setIsShowSpeakerClose(false);
            setIsShowUsedTTSModal(false);
            setIsShowChangedToHeadphoneModal(false);
            footerCloseModalFunction?.();
        } else {
            setIsShowUsedTTSModal(true);
            setIsShowHeadPhoneGuide(false);
            setIsShowSpeakerClose(false);
            setIsShowChangedToHeadphoneModal(false);
            footerCloseModalFunction?.();
            handleBarrierFreeUsed();
            updateConfig({
                tts: {
                    ...tts,
                    voice: true,
                },
            });
        }
    }, [
        tts,
        updateConfig,
        setIsShowSpeakerClose,
        setIsShowHeadPhoneGuide,
        setIsShowUsedTTSModal,
        isShowHeadPhoneGuide,
        isShowUsedTTSModal,
        isShowHeadPhoneStop,
        isShowSpeakerClose,
    ]);

    const handleSwitchToHeadphone = useCallback(() => {
        setIsShowUsedTTSModal(true);
        setIsShowHeadPhoneGuide(false);
        setIsShowSpeakerClose(false);
        setIsShowChangedToHeadphoneModal(false);
        footerCloseModalFunction?.();

        updateConfig({
            tts: {
                ...tts,
                speaker: 'headphone',
                voice: true,
            },
        });
        postAudioDeviceChangeMessage(true);
    }, [tts, updateConfig]);

    const handleSwitchToExternal = useCallback(() => {
        setIsShowHeadPhoneStop(true);
        setIsShowUsedTTSModal(false);
        setIsShowHeadPhoneGuide(false);
        setIsShowSpeakerClose(false);
        setIsShowChangedToHeadphoneModal(false);
        footerCloseModalFunction?.();
        updateConfig({
            tts: {
                ...tts,
                speaker: 'external',
                voice: true,
            },
        });
        postAudioDeviceChangeMessage(false);
    }, [tts, updateConfig, footerCloseModalFunction]);

    const handleAnnounce = useCallback(() => {
        if (!isHomePage || isBarrierFreeUsed) return;

        personDetectedRef.current = true;
        hasUsedRef.current = false;
        speakAnnouncement(t('accessibility.tts.announcement_use_headphone'));
        startRepeatAnnouncement();
    }, [speakAnnouncement, t, startRepeatAnnouncement, isHomePage, isBarrierFreeUsed]);

    const handleAnnounceRepeat = useCallback(() => {
        if (!personDetectedRef.current && !hasUsedRef.current) {
            handleAnnounce();
        }
    }, [handleAnnounce]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        if (isHomePage && personDetectedRef.current && !isBarrierFreeUsed) {
            timeoutId = setTimeout(() => {
                handleAnnounce();
            }, 60000); // 1 minute
        } else {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [handleAnnounce, isHomePage, isBarrierFreeUsed]);

    const handleAnnounceStop = useCallback(() => {
        clearAllTimers();
        personDetectedRef.current = false;
        hasUsedRef.current = false;
    }, [clearAllTimers]);

    const handleBackToPrevious = useCallback(() => {
        const isShowModal = document.querySelector('.tts-modal');
        if (isShowModal) {
            return;
        }

        navigate(-1);
    }, [navigate]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!barrierFree) return;
            const excludedKeys = ['F13', 'F14', 'F15'];
            if (!excludedKeys.includes(e.key)) {
                hasUsedRef.current = true;
            }

            switch (e.key) {
                case 'h':
                    handleToggleTTS();
                    break;
                case 'F13':
                    handleAnnounce();
                    break;
                case 'F14':
                    handleAnnounceRepeat();
                    break;
                case 'F15':
                    handleAnnounceStop();
                    break;
                case 'Backspace':
                    playTick("ting")
                    handleBackToPrevious();
                    break;
                default:
                    break;
            }
        };

        const handleClick = () => {
            if (personDetectedRef.current) {
                hasUsedRef.current = true;
            }
        };

        const handleTouchStart = () => {
            if (personDetectedRef.current) {
                hasUsedRef.current = true;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('click', handleClick, true);
        window.addEventListener('touchstart', handleTouchStart, true);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('click', handleClick, true);
            window.removeEventListener('touchstart', handleTouchStart, true);
        };
    }, [
        barrierFree,
        handleToggleTTS,
        handleSwitchToHeadphone,
        handleSwitchToExternal,
        handleAnnounce,
        handleAnnounceRepeat,
        handleAnnounceStop,
        handleBackToPrevious,
    ]);

    const handleUsedTTSModalClose = () => {
        setIsShowUsedTTSModal(false);
        if (tts.speaker !== 'headphone') {
            updateConfig({
                tts: {
                    ...tts,
                    voice: false,
                },
            });
        }
    };

    const handleUsedTTSModalHomeRedirect = useCallback(() => {
        setIsShowUsedTTSModal(false);
        navigate(PATHS.HOME.INDEX);
        updateConfig({
            tts: {
                ...tts,
                voice: true,
            },
        });
    }, [navigate, tts, updateConfig]);

    const handleUsedTTSModalConfirm = () => {
        setIsShowUsedTTSModal(false);
        updateConfig({
            tts: {
                ...tts,
                voice: true,
            },
        });
    };

    const handleSpeakerModalClose = () => {
        setIsShowHeadPhoneGuide(false);
    };

    const handleCloseHeadPhoneStopModal = () => {
        setIsShowHeadPhoneStop(false);
        clearCountdown();
        updateConfig({
            tts: {
                ...tts,
                speaker: 'external',
                voice: true,
            },
        });
    };

    const handleUseSpeakerHeadPhoneStopModal = () => {
        setIsShowHeadPhoneStop(false);
        clearCountdown();
        updateConfig({
            tts: {
                ...tts,
                speaker: 'external',
                voice: true,
            },
        });
    };

    const handleTurnOffVoiceHeadPhoneStopModal = useCallback(() => {
        setIsShowHeadPhoneStop(false);
        clearCountdown();
        updateConfig({
            tts: {
                ...tts,
                speaker: 'external',
                voice: false,
            },
        });

        navigate(PATHS.HOME.INDEX);
        resetService.executeReset();
    }, [clearCountdown, navigate, tts, updateConfig]);

    const handleOnConfirmSpeakerCloseModal = useCallback(() => {
        setIsShowSpeakerClose(false);
        updateConfig({ tts: { ...tts, voice: false } });
        navigate(PATHS.HOME.INDEX);
    }, [tts, updateConfig, navigate]);

    const handleBarrierFreeUsed = () => {
        setIsBarrierFreeUsed(true);
    };

    const handleBarrierFreeReset = () => {
        setIsBarrierFreeUsed(false);
    };

    const handleFooterCloseModal = useCallback((closeFunction: () => void) => {
        setFooterCloseModalFunction(() => closeFunction);
    }, []);

    // Check if all conditions are back to default values
    const checkDefaultConditions = useCallback(() => {
        const currentLanguage = (storeLangs?.find((lang) => lang.mainLang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
        const isDefaultContrast = contrast === false;
        const isDefaultScreenMode = screenMode === 'fullscreen';
        const isDefaultZoomIn = zoomIn === false;
        const isDefaultVolume = volume === CONFIG_CONSTANTS.DEFAULT_VOLUME;
        const isDefaultVoice = tts.voice === false;
        const isDefaultLanguage = language === currentLanguage;

        return isDefaultContrast && isDefaultScreenMode && isDefaultZoomIn && isDefaultVolume && isDefaultVoice && isDefaultLanguage;
    }, [contrast, screenMode, zoomIn, volume, tts.voice, language, storeLangs]);

    const handleUsbMediaChange = useCallback(
        (message: { type: WPFMessageType; action: string }) => {
            if (message?.action === UsbMediaConnectStatus.Connected) {
                postAudioDeviceChangeMessage(true);
                setTimeout(() => {
                    updateConfig({
                        tts: {
                            ...tts,
                            speaker: 'headphone',
                            voice: true,
                        },
                    });

                    if (!tts.voice) {
                        setIsShowUsedTTSModal(true);
                        setIsShowHeadPhoneGuide(false);
                        setIsShowChangedToHeadphoneModal(false);
                        setIsShowHeadPhoneStop(false);
                        setIsShowSpeakerClose(false);
                        footerCloseModalFunction?.();
                    } else {
                        setIsShowChangedToHeadphoneModal(true);
                        setIsShowUsedTTSModal(false);
                        setIsShowHeadPhoneGuide(false);
                        setIsShowHeadPhoneStop(false);
                        setIsShowSpeakerClose(false);
                        footerCloseModalFunction?.();
                    }
                }, 500);
            }

            if (message?.action === UsbMediaConnectStatus.Disconnected) {
                if (tts.voice) {
                    setIsShowHeadPhoneStop(true);
                    setIsShowUsedTTSModal(false);
                    setIsShowHeadPhoneGuide(false);
                    setIsShowChangedToHeadphoneModal(false);
                    setIsShowSpeakerClose(false);
                    footerCloseModalFunction?.();
                    startCountdown();
                }
            }
        },
        [tts, startCountdown, updateConfig, footerCloseModalFunction]
    );

    useEffect(() => {
        const handler = async (event: MessageEvent) => {
            const message = event.data;

            switch (message.type) {
                case WPFMessageType.HeadphoneInOut:
                    handleUsbMediaChange(message);
                    break;
                default:
                    break;
            }
        };

        window.chrome?.webview?.addEventListener('message', handler);
        return () => {
            window.chrome?.webview?.removeEventListener('message', handler);
        };
    }, [handleUsbMediaChange]);

    // Monitor config changes and call reset when all conditions are default
    useEffect(() => {
        if (checkDefaultConditions()) {
            handleBarrierFreeReset();
        }
    }, [checkDefaultConditions]);

    // Cleanup timers on component unmount
    useEffect(() => {
        return () => {
            clearAllTimers();
        };
    }, [clearAllTimers]);

    return (
        <>
            <div
                style={{
                    width: ZOOM_LEVELS[zoomLevel - 1] > 1 ? `${ZOOM_LEVELS[zoomLevel - 1] * 100}vw` : '100vw',
                    height: ZOOM_LEVELS[zoomLevel - 1] > 1 ? `${ZOOM_LEVELS[zoomLevel - 1] * 100}vh` : '100vh',
                    overflow: ZOOM_LEVELS[zoomLevel - 1] > 1 ? 'auto' : 'hidden',
                }}
            >
                <div
                    className={clsx(styles['kiosk-layout'], {
                        [styles['kiosk-layout--horizontal']]: isHorizontal,
                        [styles['kiosk-layout--vertical']]: !isHorizontal,
                    })}
                    ref={(element) => {
                        layoutRef.current = element;
                    }}
                    style={{
                        transform: `scale(${ZOOM_LEVELS[zoomLevel - 1]})`,
                        transformOrigin: 'top left',
                    }}
                >
                    {!isHomePage && !shouldHideCountdownButton() && (
                        <div
                            style={{
                                position: 'absolute',
                                top:
                                    screenMode === 'fullscreen'
                                        ? `${getHSize(24, baseHeight)}`
                                        : isHorizontal
                                            ? `${getHSize(194, baseHeight)}`
                                            : `${getHSize(892, baseHeight)}`,
                                right: isHorizontal ? `${getSize(282, baseWidth)}` : `${getSize(24, baseWidth)}`,
                                zIndex: 50,
                            }}
                        >
                            <CountdownButton onZoomButtonsOpen={handleZoomButtonsOpen} />
                        </div>
                    )}

                    {/* Hidden CountdownButton for contrast mode - runs in background only */}
                    {isBarrierFreeUsed && isHomePage && !shouldHideCountdownButton() && (
                        <div style={{ display: 'none' }}>
                            <CountdownButton
                                onZoomButtonsOpen={handleZoomButtonsOpen}
                                closeModal={() => {
                                    setIsZoomButtonsOpen(false);
                                    handleSpeakerModalClose();
                                    handleUsedTTSModalClose();
                                    setIsShowHeadPhoneStop(false);
                                    setIsShowSpeakerClose(false);
                                    // Also close FooterLayout modals
                                    footerCloseModalFunction?.();
                                }}
                            />
                        </div>
                    )}

                    {!isHorizontal && isHomePage && (
                        <div className={styles['kiosk-layout__header']} data-nav="header">
                            <Header />
                        </div>
                    )}

                    <div className={clsx(styles['kiosk-layout__gnb-wrapper'], 'focus-border')} data-nav="gnb">
                        <div className={clsx(styles['kiosk-layout__gnb'], 'scrollbar-hide')}>
                            <GNBLayout gnbItems={gnbItemsWithSvg} />
                        </div>
                    </div>

                    <div className={clsx(styles['kiosk-layout__content'], 'focus-border')} data-nav="content">
                        <main className={styles['kiosk-layout__content-area']} role="main">
                            {children}
                        </main>
                    </div>

                    <div className={clsx(styles['kiosk-layout__footer'], 'focus-border')} data-nav="footer">
                        <FooterLayout
                            onZoomButtonsOpen={handleZoomButtonsOpen}
                            isZoomButtonsOpen={isZoomButtonsOpen}
                            handleZoomClose={handleZoomClose}
                            onBarrierFreeUsed={handleBarrierFreeUsed}
                            closeModal={handleFooterCloseModal}
                            onBarrierFreeReset={handleBarrierFreeReset}
                        />
                    </div>

                    {/* Headphone Guide Modal */}
                    <HeadPhoneGuideModal isOpen={isShowHeadPhoneGuide} onClose={handleSpeakerModalClose} language={languageCheck} />

                    {/* Used TTS Modal */}
                    <UsedTTSModal
                        isOpen={isShowUsedTTSModal}
                        onHomeRedirect={handleUsedTTSModalHomeRedirect}
                        onClose={handleUsedTTSModalClose}
                        onConfirm={handleUsedTTSModalConfirm}
                    />

                    {/* Headphone Stop Modal */}
                    <HeadPhoneStopModal
                        remaining={remaining}
                        isOpen={isShowHeadPhoneStop}
                        onClose={handleCloseHeadPhoneStopModal}
                        onUseSpeaker={handleUseSpeakerHeadPhoneStopModal}
                        onTurnOffVoice={handleTurnOffVoiceHeadPhoneStopModal}
                    />
                    {/* Speaker Close Modal */}
                    <SpeakerCloseModal isOpen={isShowSpeakerClose} onClose={() => setIsShowSpeakerClose(false)} onConfirm={handleOnConfirmSpeakerCloseModal} />

                    <ChangedToHeadphoneModal isOpen={isShowChangedToHeadphoneModal} onClose={() => setIsShowChangedToHeadphoneModal(false)} onConfirm={() => { navigate(PATHS.HOME.INDEX); setIsShowChangedToHeadphoneModal(false) }} />

                    {/* Portal container for TimeExtensionModal - must be inside scaled div but outside stacking context */}
                    <div id="time-extension-modal-root" style={{ position: 'relative', zIndex: 70 }} />
                </div>
            </div>
            <ZoomButtons isOpen={isZoomButtonsOpen} onClose={handleZoomClose} onContrast={handleContrast} onZoom={handleZoom} isZoomedIn={zoomIn} />
        </>
    );
};
